import { useMemo } from 'react';
import { Alert } from '@accure/ui';

import { SiteMetadata as Metadata } from '@/api/fe-api/domain/entities/site-metadata';
import { GetAllSiteMetadata } from '@/api/fe-api/view/queries/get-all-site-metadata';
import { UnreachableCaseError } from '@/utils/errors/unreachable-case-error';

import { SiteMetadataSection } from './site-metadata-section';
import { SiteMetadataSources } from './site-metadata-sources';

export type SiteMetadataProps = {
  siteId: string;
};

export function SiteMetadata({ siteId }: SiteMetadataProps) {
  const siteMetadata = GetAllSiteMetadata.useQuery({ siteId });

  if (siteMetadata.isLoading) {
    return <div>Loading...</div>;
  }

  if (siteMetadata.isError || !siteMetadata.data) {
    return <Alert severity="error">Unable to load the metadata</Alert>;
  }

  if (siteMetadata.data.isErr()) {
    if (siteMetadata.data.error.type === 'NOT_FOUND') {
      return <p>Site not found</p>;
    }

    throw new UnreachableCaseError(siteMetadata.data.error.type);
  }

  const { sections } = siteMetadata.data.value;

  return <SiteMetadataSections siteId={siteId} sections={sections} />;
}

type SiteMetadataSectionsProps = {
  siteId: string;
  sections: Metadata.Section[];
};

function SiteMetadataSections({ siteId, sections }: SiteMetadataSectionsProps) {
  const { documents, documentIndexes } = useMemo(
    function () {
      const map = new Map<string, Metadata.Document>();

      for (const section of sections) {
        for (const group of section.groups) {
          for (const metadata of group.rows) {
            if (metadata.document) {
              map.set(metadata.document.id, metadata.document);
            }
          }
        }
      }

      const documentIndexes = Object.fromEntries(
        Array.from(map.values()).map((document, index) => [document.id, index]),
      );

      const documents = Array.from(map.values());

      return { documents, documentIndexes };
    },
    [sections],
  );

  return (
    <div data-testid="site-metadata" className="mt-10 flex flex-col gap-4">
      <div>
        <h3 className="mb-6 block text-2xl font-medium leading-6 text-gray-900">
          Metadata
        </h3>
        <div className="mb-8 flex flex-col gap-4">
          {sections.map((section) => (
            <SiteMetadataSection
              key={section.key}
              section={section}
              documentIndexes={documentIndexes}
            />
          ))}
        </div>
      </div>
      {documents.length > 0 ? (
        <SiteMetadataSources
          siteId={siteId}
          documents={documents}
          documentIndexes={documentIndexes}
        />
      ) : null}
    </div>
  );
}
